import { useState } from 'react';
import { Button, Group, Stack, TextInput, Textarea, Text, Code } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconMail, IconCopy, IconCheck } from '@tabler/icons-react';
import {
  buildMailtoUrl,
  truncateForMailto,
  formatEmailSubject,
  type EmailContentType,
} from 'src/common/utils/email/mailto';
import { ExampleStack, type Example } from '../';

function mailtoDemo(): Example {
  const MailtoDemo = () => {
    const [recipient, setRecipient] = useState('client@example.com');
    const [subject, setSubject] = useState('Meeting Notes - John Smith - 15 Jan 2025');
    const [body, setBody] = useState(
      'Hi,\n\nPlease find the meeting notes from our recent discussion.\n\nBest regards',
    );

    const mailtoUrl = buildMailtoUrl(recipient, subject, body);

    return (
      <Stack gap="sm">
        <TextInput
          label="Recipient"
          value={recipient}
          onChange={e => setRecipient(e.target.value)}
        />
        <TextInput label="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
        <Textarea
          label="Body"
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={5}
          styles={{ input: { resize: 'vertical' } }}
        />
        <Text size="sm" c="dimmed">
          Generated URL ({mailtoUrl.length} chars):
        </Text>
        <Code block style={{ wordBreak: 'break-all' }}>
          {mailtoUrl}
        </Code>
        <Button component="a" href={mailtoUrl} leftSection={<IconMail size={16} />} w="fit-content">
          Open in email client
        </Button>
      </Stack>
    );
  };

  return {
    description: 'buildMailtoUrl - constructs mailto URL with proper encoding',
    render: <MailtoDemo />,
  };
}

function truncateDemo(): Example {
  const TruncateDemo = () => {
    const [content, setContent] = useState(
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. '.repeat(50),
    );
    const [maxLength, setMaxLength] = useState(200);

    const truncated = truncateForMailto(content, maxLength);

    return (
      <Stack gap="sm">
        <Textarea
          label={`Content (${content.length} chars)`}
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={5}
          styles={{ input: { resize: 'vertical' } }}
        />
        <TextInput
          label="Max length"
          type="number"
          value={maxLength}
          onChange={e => setMaxLength(Number(e.target.value))}
          w={150}
        />
        <Text size="sm" c="dimmed">
          Truncated ({truncated.length} chars):
        </Text>
        <Code block style={{ whiteSpace: 'pre-wrap' }}>
          {truncated}
        </Code>
      </Stack>
    );
  };

  return {
    description: 'truncateForMailto - truncates content and appends suffix when needed',
    render: <TruncateDemo />,
  };
}

function subjectDemo(): Example {
  const SubjectDemo = () => {
    const [type, setType] = useState<EmailContentType>('Meeting Notes');
    const [clientName, setClientName] = useState('John Smith');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

    const subject = formatEmailSubject(type, clientName, new Date(date));

    return (
      <Stack gap="sm">
        <Group>
          <Button
            variant={type === 'Meeting Notes' ? 'filled' : 'outline'}
            onClick={() => setType('Meeting Notes')}
            size="xs"
          >
            Meeting Notes
          </Button>
          <Button
            variant={type === 'Meeting Agenda' ? 'filled' : 'outline'}
            onClick={() => setType('Meeting Agenda')}
            size="xs"
          >
            Meeting Agenda
          </Button>
          <Button
            variant={type === 'Data Export' ? 'filled' : 'outline'}
            onClick={() => setType('Data Export')}
            size="xs"
          >
            Data Export
          </Button>
        </Group>
        <TextInput
          label="Client name"
          value={clientName}
          onChange={e => setClientName(e.target.value)}
        />
        <TextInput label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} />
        <Text size="sm" c="dimmed">
          Generated subject:
        </Text>
        <Code>{subject}</Code>
      </Stack>
    );
  };

  return {
    description: 'formatEmailSubject - generates subject line from type, client, and date',
    render: <SubjectDemo />,
  };
}

function clipboardDemo(): Example {
  const ClipboardDemo = () => {
    const subjectClipboard = useClipboard({ timeout: 2000 });
    const bodyClipboard = useClipboard({ timeout: 2000 });

    const subject = 'Meeting Notes - John Smith - 15 Jan 2025';
    const body = 'Hi,\n\nPlease find the meeting notes attached.\n\nBest regards';

    return (
      <Stack gap="sm">
        <Text size="sm">Using Mantine&apos;s useClipboard hook:</Text>
        <Group>
          <Button
            leftSection={subjectClipboard.copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            color={subjectClipboard.copied ? 'teal' : 'blue'}
            onClick={() => subjectClipboard.copy(subject)}
          >
            {subjectClipboard.copied ? 'Copied!' : 'Copy subject'}
          </Button>
          <Button
            leftSection={bodyClipboard.copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            color={bodyClipboard.copied ? 'teal' : 'blue'}
            onClick={() => bodyClipboard.copy(body)}
          >
            {bodyClipboard.copied ? 'Copied!' : 'Copy body'}
          </Button>
        </Group>
        <Code block>Subject: {subject}</Code>
        <Code block style={{ whiteSpace: 'pre-wrap' }}>
          Body: {body}
        </Code>
      </Stack>
    );
  };

  return {
    description: 'useClipboard (Mantine) - copy subject and body separately',
    render: <ClipboardDemo />,
  };
}

export default function EmailSandbox() {
  return <ExampleStack examples={[mailtoDemo(), truncateDemo(), subjectDemo(), clipboardDemo()]} />;
}
